import { zodResolver } from '@hookform/resolvers/zod';
import { useDeployFromMain } from '@sunstone/api-client/hooks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Switch,
} from '@sunstone/ui';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { useAuthToken } from '@/features/auth';

const schema = z.object({
  installerBranch: z.string().optional(),
  serviceName: z.string().min(1, 'Service name is required'),
  serviceBranch: z.string().min(1, 'Service branch is required'),
  imageTag: z.string().optional(),
  chartVersion: z.string().optional(),
  refreshIngress: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface SingleServiceDeployDialogProps {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SingleServiceDeployDialog({
  workspaceId,
  open,
  onOpenChange,
}: SingleServiceDeployDialogProps) {
  const { token } = useAuthToken();
  const deployFromMain = useDeployFromMain(token);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      installerBranch: '',
      serviceName: '',
      serviceBranch: '',
      imageTag: '',
      chartVersion: '',
      refreshIngress: false,
    },
  });

  function onSubmit(values: FormValues) {
    deployFromMain.mutate(
      {
        workspaceId,
        installerBranch: values.installerBranch,
        serviceName: values.serviceName,
        serviceBranch: values.serviceBranch,
        imageTag: values.imageTag,
        chartVersion: values.chartVersion,
        refreshIngress: values.refreshIngress,
      },
      {
        onSuccess: () => {
          toast.success('Deploy dispatched.');
          form.reset();
          onOpenChange(false);
        },
        onError: (err) => toast.error(err.message ?? 'Failed to dispatch deploy.'),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Single Service Deploy</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="serviceName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Service Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. baic-api" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="serviceBranch"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Service Branch <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. feature/my-fix" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="installerBranch"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Installer Branch</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Defaults to current release's branch" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="imageTag"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Image Tag</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Provide chart or image" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="chartVersion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chart Version</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Provide chart or image" />
                  </FormControl>
                  <FormDescription>
                    Provide image or chart if Service branch is not main or release branch
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="refreshIngress"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between space-y-0">
                  <FormLabel className="font-normal">Refresh Ingress</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      aria-label="Refresh ingress"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={deployFromMain.isPending}>
              {deployFromMain.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Dispatching…
                </>
              ) : (
                'Deploy'
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
